import { useEffect, useState } from "react";
import { RefreshCw, Trash2, UserRound, Users2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SEL = "h-10 w-full rounded-control border border-line bg-white px-3 text-sm";
const CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*?-_+=";

export const generatePassword = (len = 20) => {
  const a = new Uint32Array(len);
  window.crypto.getRandomValues(a);
  return Array.from(a, (n) => CHARS[n % CHARS.length]).join("");
};

const CompanyPick = ({ value, onPick }) => {
  const [q, setQ] = useState(""), [res, setRes] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setRes([]); return undefined; }
    const t = setTimeout(() => api.get("/crm/companies", { params: { q, limit: 8 } }).then((r) => setRes(r.data.items || [])).catch(() => setRes([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative space-y-1.5">
      <Label className="text-xs">Cliente collegato (facoltativo){value?.name ? `: ${value.name}` : ""}</Label>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente" data-testid="vault-company-search" />
        {value?.id && <Button type="button" variant="outline" onClick={() => onPick(null)} data-testid="vault-company-clear">Rimuovi</Button>}
      </div>
      {res.length > 0 && <ul className="absolute z-20 w-full rounded-control border border-line bg-white shadow-lg">{res.map((c) => (
        <li key={c.id}><button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => { onPick(c); setQ(""); }} data-testid={`vault-company-${c.id}`}>{c.name}</button></li>))}</ul>}
    </div>
  );
};

const SharesEditor = ({ meta, shares, onChange }) => {
  const [pick, setPick] = useState("");
  const name = (s) => (s.type === "user" ? meta.users.find((u) => u.id === s.id)?.full_name : meta.roles.find((r) => r.key === s.id)?.name) || s.id;
  const add = () => {
    if (!pick) return;
    const [type, id] = pick.split(":");
    if (!shares.some((s) => s.type === type && s.id === id)) onChange([...shares, { type, id, level: "view" }]);
    setPick("");
  };
  return (
    <div className="space-y-2" data-testid="vault-shares-editor">
      <Label className="text-xs">Chi può vederla</Label>
      <div className="flex gap-2">
        <select className={SEL} value={pick} onChange={(e) => setPick(e.target.value)} data-testid="vault-share-select">
          <option value="">Aggiungi utente o ruolo…</option>
          <optgroup label="Ruoli">{meta.roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.name} (intero ruolo)</option>)}</optgroup>
          <optgroup label="Utenti">{meta.users.map((u) => <option key={u.id} value={`user:${u.id}`}>{u.full_name} · {u.email}</option>)}</optgroup>
        </select>
        <Button type="button" variant="outline" onClick={add} data-testid="vault-share-add">Aggiungi</Button>
      </div>
      {!shares.length && <p className="text-xs text-slate-500">Visibile solo a te (e ai Super Admin).</p>}
      <ul className="space-y-1">{shares.map((s, i) => (
        <li key={`${s.type}${s.id}`} className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-1.5 text-sm" data-testid={`vault-share-${s.type}-${s.id}`}>
          {s.type === "role" ? <Users2 className="h-4 w-4 text-slate-500" /> : <UserRound className="h-4 w-4 text-slate-500" />}
          <span className="min-w-0 flex-1 truncate">{name(s)}</span>
          <select className="h-8 rounded-md border border-line bg-white px-2 text-xs" value={s.level} data-testid={`vault-share-level-${s.id}`}
            onChange={(e) => onChange(shares.map((x, j) => (j === i ? { ...x, level: e.target.value } : x)))}>
            <option value="view">Può vedere</option><option value="edit">Può modificare</option>
          </select>
          <button type="button" onClick={() => onChange(shares.filter((_, j) => j !== i))} data-testid={`vault-share-remove-${s.id}`} aria-label="Rimuovi"><Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" /></button>
        </li>))}</ul>
    </div>
  );
};

export const VaultDialog = ({ open, onOpenChange, item, meta, onDone }) => {
  const [f, setF] = useState({}), [busy, setBusy] = useState(false);
  const owner = !item || item.my_level === "owner";
  useEffect(() => {
    if (!open) return;
    setF(item ? { ...item, password: "", notes: "", shares: item.shares || [] } : { title: "", category: "Altro", url: "", username: "", password: "", notes: "", shares: [], company_id: null });
    if (item?.has_notes) api.post(`/vault/${item.id}/reveal`).then((r) => setF((x) => ({ ...x, notes: r.data.notes, _notes_loaded: true }))).catch(() => {});
  }, [open, item]);
  const s = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    const body = { title: f.title, category: f.category, url: f.url, username: f.username, company_id: f.company_id || null,
      ...(owner ? { shares: f.shares } : {}), ...(f.password ? { password: f.password } : !item ? { password: "" } : {}),
      ...(!item || f._notes_loaded || !item.has_notes ? { notes: f.notes } : {}) };
    try {
      await (item ? api.patch(`/vault/${item.id}`, body) : api.post("/vault", body));
      toast.success(item ? "Voce aggiornata" : "Password salvata"); onOpenChange(false); onDone();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="vault-dialog">
        <DialogHeader><DialogTitle>{item ? "Modifica password" : "Nuova password"}</DialogTitle>
          <DialogDescription>Password e note sono cifrate. Ogni visualizzazione viene registrata.</DialogDescription></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Titolo</Label><Input value={f.title || ""} onChange={s("title")} data-testid="vault-title-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Categoria</Label>
            <select className={SEL} value={f.category || "Altro"} onChange={s("category")} data-testid="vault-category-select">{meta.categories.map((c) => <option key={c}>{c}</option>)}</select></div>
          <div className="space-y-1.5"><Label className="text-xs">URL</Label><Input value={f.url || ""} onChange={s("url")} placeholder="https://" data-testid="vault-url-input" /></div>
          <div className="sm:col-span-2"><CompanyPick value={f.company_id ? { id: f.company_id, name: f.company_name } : null} onPick={(c) => setF((x) => ({ ...x, company_id: c?.id || null, company_name: c?.name || null }))} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Username</Label><Input value={f.username || ""} onChange={s("username")} autoComplete="off" data-testid="vault-username-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Password{item?.has_password ? " (vuoto = invariata)" : ""}</Label>
            <div className="flex gap-2"><Input value={f.password || ""} onChange={s("password")} autoComplete="new-password" className="font-mono" data-testid="vault-password-input" />
              <Button type="button" variant="outline" size="icon" onClick={() => setF((x) => ({ ...x, password: generatePassword() }))} title="Genera password sicura" data-testid="vault-generate-btn"><RefreshCw className="h-4 w-4" /></Button></div></div>
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Note cifrate</Label><Textarea rows={3} value={f.notes || ""} onChange={s("notes")} data-testid="vault-notes-input" /></div>
          {owner ? <div className="sm:col-span-2"><SharesEditor meta={meta} shares={f.shares || []} onChange={(v) => setF((x) => ({ ...x, shares: v }))} /></div>
            : <p className="text-xs text-slate-500 sm:col-span-2">Solo il proprietario può cambiare chi vede questa voce.</p>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vault-cancel-btn">Annulla</Button>
          <Button onClick={save} disabled={busy || !f.title?.trim()} data-testid="vault-save-btn">{busy ? "Salvataggio…" : "Salva"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
